import { useCallback, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { isOptionGroup } from 'UIComponents/types/OptionTypes';
import { H5 } from 'foundations-components/transitional/components/H5';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { List } from 'foundations-components/transitional/components/List';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OptionCheckbox = ({
  onChange,
  option,
  selectedOptions,
  selectionLimit
}) => {
  const {
    value
  } = option;
  const isSelected = !!selectedOptions.find(selection => selection.value === value);
  const isDisabled = !!selectionLimit && selectedOptions.length >= selectionLimit && !isSelected;
  return /*#__PURE__*/_jsx(Tooltip, {
    disabled: !isDisabled,
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-ui-components.option-config-modal.selectDisabled",
      options: {
        limit: selectionLimit
      }
    }),
    children: /*#__PURE__*/_jsx(Checkbox, {
      "data-test-id": `option-checkbox-${value}`,
      checked: isSelected,
      disabled: isDisabled,
      onChange: ({
        target: {
          checked
        }
      }) => {
        onChange(option, checked);
      },
      children: /*#__PURE__*/_jsx(TruncateString, {
        children: option.text
      })
    })
  });
};
const isSearchResult = (option, searchTerm) => {
  const {
    text
  } = option;
  return !!text && text.toLocaleLowerCase().includes(searchTerm);
};
const OrderSelectionInput = ({
  getInitialOptionPosition,
  onSelectionChange,
  options,
  searchTerm,
  selectedOptions,
  selectionLimit
}) => {
  const results = useMemo(() => options.reduce((memo, option) => {
    if (isOptionGroup(option)) {
      const groupResults = option.options.filter(childOption => isSearchResult(childOption, searchTerm));
      return groupResults.length > 0 ? memo.concat(Object.assign({}, option, {
        options: groupResults
      })) : memo;
    }
    return isSearchResult(option, searchTerm) /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */ ? memo.concat(option) : memo;
  }, []), [options, searchTerm]);
  const handleSelectionChange = useCallback((option, checked) => {
    if (checked) {
      const newSelectedOptions = getInitialOptionPosition && getInitialOptionPosition(option) === 'first' ? [option].concat(selectedOptions) : selectedOptions.concat(option);
      onSelectionChange(newSelectedOptions);
    } else {
      onSelectionChange(selectedOptions.filter(selection => selection.value !== option.value));
    }
  }, [getInitialOptionPosition, onSelectionChange, selectedOptions]);
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "option-selection-input",
    children: results.map(option => {
      if (!isOptionGroup(option)) {
        return /*#__PURE__*/_jsx(OptionCheckbox, {
          onChange: handleSelectionChange,
          option: option,
          selectedOptions: selectedOptions,
          selectionLimit: selectionLimit
        }, `option-${option.value}`);
      }
      return /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(H5, {
          marginBlock: "100",
          children: /*#__PURE__*/_jsx(TruncateString, {
            children: option.text
          })
        }), /*#__PURE__*/_jsx(List, {
          marginBlockEnd: "200",
          wrapChildren: true,
          children: option.options.map(childOption => /*#__PURE__*/_jsx(OptionCheckbox, {
            onChange: handleSelectionChange,
            option: childOption,
            selectedOptions: selectedOptions,
            selectionLimit: selectionLimit
          }, `option-${childOption.value}`))
        })]
      }, `${option.text}-options`);
    })
  });
};
export default OrderSelectionInput;